import type { GeographicRing } from './types';

/**
 * Inside-the-Ranch vs outside-the-Ranch classification.
 *
 * The single question this app's browse surfaces need to answer first is
 * "can I walk/golf-cart to this, or do I have to drive out?". Category
 * ("food", "medical") is secondary to that. So every store and service is
 * segregated into exactly two groups.
 *
 * WHY THIS IS DERIVED RATHER THAN A COLUMN
 * ----------------------------------------
 * `places.geographic_ring` already exists and is indexed, but production
 * data uses values the `GeographicRing` union does not declare — as of
 * 2026-09-02 the live table contains `inside_babcock_ranch` (5 rows) and
 * `wider_region` (2 rows), neither of which is in the type. `places-db.ts`
 * casts the raw column with `as GeographicRing`, so those values flow
 * through untyped. Rather than migrate production data as a side effect of
 * a UI change, this module maps every value actually observed in prod —
 * declared or not — onto one of two groups. That keeps the grouping correct
 * today and keeps the data cleanup a separate, reviewable change.
 */

export type LocalityGroup = 'inside' | 'outside';

/** Chip selection: 'both' is Everywhere; omit from the URL when both. */
export type LocalityFilter = 'both' | LocalityGroup;

/**
 * Compact segment labels. Longer copy ("In the Ranch" / "Outside of the
 * Ranch") does not fit three nowrap segments on a 390px phone — Places
 * already uses these short forms for that reason.
 */
export const LOCALITY_FILTERS: { key: LocalityFilter; label: string }[] = [
  { key: 'both', label: 'Everywhere' },
  { key: 'inside', label: 'In Ranch' },
  { key: 'outside', label: 'Outside' },
];

export const LOCALITY_LABELS: Record<LocalityGroup, string> = {
  inside: 'Inside Babcock Ranch',
  outside: 'Outside the Ranch',
};

export const LOCALITY_BLURBS: Record<LocalityGroup, string> = {
  inside: 'Walkable, bikeable, or a short golf-cart ride',
  outside: 'Requires driving out of the Ranch',
};

/**
 * Every `geographic_ring` value that means "inside the Babcock Ranch
 * development". Includes values that are live in production but absent
 * from the `GeographicRing` union (see module note above).
 *
 * `babcock_national` counts as inside: Babcock National is a gated golf
 * community within the Babcock Ranch development, not a separate town.
 */
const INSIDE_RINGS: ReadonlySet<string> = new Set<string>([
  'babcock_ranch',
  'inside_babcock_ranch',
  'babcock_national',
]);

/**
 * Places whose `geographic_ring` is wrong in production and which are
 * verifiably inside the Ranch.
 *
 * Babcock Ranch's postal city is "Punta Gorda, FL 33982", so seeding
 * assigned `punta_gorda` to some businesses that are physically in the
 * Ranch's own retail corridor. Each entry below was confirmed two ways:
 * its street address sits in the 420xx Cypress Parkway block (Publix is
 * 42051, Sunshine Ace is 42080), and it appears on the official tenant
 * list at babcockranch.com/our-town/commerce-cuisine/.
 *
 * Deliberately NOT included, despite similar names:
 *   - `ace-hardware-of-punta-gorda`  — 208 W Marion Ave, 33950 (downtown
 *     Punta Gorda). The in-Ranch store is a separate row,
 *     `sunshine-ace-hardware-babcock-ranch` at 42080 Cypress Parkway.
 *   - `the-ups-store-bermont-road`   — 27650 Bermont Road, a different
 *     corridor ~18 km north. The Ranch's own UPS Store is not in the
 *     database.
 *
 * TODO: remove these overrides once `geographic_ring` is corrected in D1.
 */
const INSIDE_SLUG_OVERRIDES: ReadonlySet<string> = new Set<string>([
  'panera-bread-babcock-ranch',
  'tampa-general-medical-group-babcock-ranch-primary-care-lab',
]);

/** Minimal shape needed to classify — accepts Place, PlaceWithState, or a row. */
interface LocalityInput {
  slug?: string | null;
  geographic_ring?: GeographicRing | string | null;
}

export function localityOf(place: LocalityInput): LocalityGroup {
  if (place.slug && INSIDE_SLUG_OVERRIDES.has(place.slug)) return 'inside';
  const ring = place.geographic_ring;
  if (typeof ring === 'string' && INSIDE_RINGS.has(ring)) return 'inside';
  return 'outside';
}

export function isInsideRanch(place: LocalityInput): boolean {
  return localityOf(place) === 'inside';
}

export function parseLocalityFilter(value: string | null | undefined): LocalityFilter {
  return value === 'inside' || value === 'outside' || value === 'both' ? value : 'both';
}

/** True when a place should appear under the current locality chip. */
export function placeMatchesLocality(place: LocalityInput, filter: LocalityFilter): boolean {
  if (filter === 'both') return true;
  return localityOf(place) === filter;
}

/**
 * Split a list into the two groups, preserving the incoming order within
 * each group so callers keep control of sorting.
 */
export function groupByLocality<T extends LocalityInput>(places: readonly T[]): Record<LocalityGroup, T[]> {
  const out: Record<LocalityGroup, T[]> = { inside: [], outside: [] };
  for (const p of places) out[localityOf(p)].push(p);
  return out;
}
